import * as React from 'react';
import { Box, Stack, ThemeProvider, Typography } from '@mui/material';
import { generateTheme } from '../themes';
import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';

const FLAG_CDN_BASE = 'https://flagcdn.com';

const buildFlagSrc = (code) => `${FLAG_CDN_BASE}/w80/${code.toLowerCase()}.png`;
const buildFlagSrcSet = (code) => `${FLAG_CDN_BASE}/w160/${code.toLowerCase()}.png 2x`;

const GlobalReachSlice = ({ data }) => {
    const {
        colorTheme = 'white',
        eyebrow,
        tagline,
        numberOverride,
        unitLabel = 'LÄNDER',
        maxVisibleFlags = 9,
        countries = []
    } = data;

    const theme = generateTheme(colorTheme);
    const containerRef = useRef(null);

    const visibleCountries = countries.slice(0, maxVisibleFlags);
    const overflowCount = Math.max(0, countries.length - visibleCountries.length);

    const targetNumber = (() => {
        const parsed = parseInt(numberOverride, 10);
        if (!Number.isNaN(parsed)) return parsed;
        return countries.length;
    })();

    useLayoutEffect(() => {
        if (!containerRef.current) return;

        const countEl = containerRef.current.querySelector('.gr-count');
        if (countEl) {
            const counter = { val: 0 };
            gsap.to(counter, {
                val: targetNumber,
                duration: 1.6,
                ease: 'power2.out',
                onUpdate: () => {
                    countEl.textContent = Math.round(counter.val);
                },
                scrollTrigger: {
                    id: 'grCount',
                    trigger: countEl,
                    start: 'top 80%',
                    markers: false
                }
            });
        }

        const flags = gsap.utils.toArray('.gr-flag', containerRef.current);
        flags.forEach((el, i) => {
            gsap.fromTo(
                el,
                { opacity: 0, y: 10 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    delay: 0.05 * i,
                    ease: 'easeIn',
                    scrollTrigger: {
                        id: 'grFlag',
                        trigger: el,
                        start: 'top 85%',
                        markers: false
                    }
                }
            );
        });
    }, [targetNumber]);

    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                sx={{
                    background: (t) => t.palette.background.default,
                    px: { xs: 2, md: 12 },
                    py: { xs: 4, md: 6 },
                    textAlign: 'center'
                }}
            >
                {eyebrow && (
                    <Typography
                        variant="overline"
                        component="span"
                        sx={{
                            display: 'block',
                            color: 'text.secondary',
                            letterSpacing: '0.15em',
                            fontSize: '0.8rem',
                            fontWeight: 400,
                            opacity: 0.6,
                            mb: { xs: 1, md: 1.5 }
                        }}
                        dangerouslySetInnerHTML={{ __html: eyebrow }}
                    />
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        lineHeight: 0.9
                    }}
                >
                    <Box
                        component="span"
                        className="gr-count"
                        sx={{
                            color: 'text.primary',
                            fontFamily: 'Kanit, sans-serif',
                            fontWeight: 900,
                            fontSize: { xs: '4.5rem', sm: '6rem', md: '7.5rem', lg: '9rem' },
                            lineHeight: 0.9,
                            letterSpacing: '-0.02em'
                        }}
                    >
                        0
                    </Box>
                    {unitLabel && (
                        <Box
                            component="span"
                            className="gr-unit"
                            sx={{
                                color: 'text.primary',
                                fontFamily: 'Kanit, sans-serif',
                                fontWeight: 900,
                                textTransform: 'uppercase',
                                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem', lg: '2.5rem' },
                                letterSpacing: '0.05em',
                                lineHeight: 1,
                                mt: { xs: 0.5, md: 1 }
                            }}
                            dangerouslySetInnerHTML={{ __html: unitLabel }}
                        />
                    )}
                </Box>

                {tagline && (
                    <Typography
                        component="p"
                        sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.95rem', md: '1.05rem' },
                            lineHeight: 1.55,
                            maxWidth: 560,
                            mx: 'auto',
                            mt: { xs: 2, md: 2.5 }
                        }}
                        dangerouslySetInnerHTML={{ __html: tagline }}
                    />
                )}

                {visibleCountries.length > 0 && (
                    <Stack
                        direction="row"
                        spacing={{ xs: 1, md: 1.5 }}
                        justifyContent="center"
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ rowGap: { xs: 1, md: 1.5 }, mt: { xs: 3, md: 4 } }}
                    >
                        {visibleCountries.map((country) => (
                            <Box
                                key={country.code}
                                component="img"
                                className="gr-flag"
                                src={buildFlagSrc(country.code)}
                                srcSet={buildFlagSrcSet(country.code)}
                                alt={country.name || country.code}
                                loading="lazy"
                                sx={{
                                    width: { xs: 36, md: 44 },
                                    height: { xs: 24, md: 28 },
                                    borderRadius: '4px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                    objectFit: 'cover',
                                    display: 'block'
                                }}
                            />
                        ))}
                        {overflowCount > 0 && (
                            <Box
                                className="gr-flag"
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: '6px',
                                    px: 1.25,
                                    py: 0.5,
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                    lineHeight: 1.5
                                }}
                            >
                                +{overflowCount}
                            </Box>
                        )}
                    </Stack>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default GlobalReachSlice;
